import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
    createFlowOfFundsMutationToast,
    showFlowOfFundsCycleOutcomeToast,
    showFlowOfFundsSavingToast,
} from '../flowOfFundsAutoSaveToast';
import {
    SaveThirdPartyPayoffRowRequest,
    ThirdPartyPayoff,
    VendorContractCell,
    VendorContractCellCommit,
    VendorContractColumn,
} from '../../../models/FlowOfFunds/flow-of-funds.model';

export const THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID =
    'third-party-payoffs-autosave-toast-container';

const FLUSH_DEBOUNCE_MS = 2000;

export type ThirdPartyPayoffRowSaveOutcome = 'ok' | 'conflict' | 'error';

export interface ThirdPartyPayoffRowSaveResult {
    outcome: ThirdPartyPayoffRowSaveOutcome;
    freshCells?: VendorContractCell[];
}

export type ThirdPartyPayoffRowSaveHandler = (
    tableRowId: number,
    request: SaveThirdPartyPayoffRowRequest
) => Promise<ThirdPartyPayoffRowSaveResult>;

export interface ThirdPartyPayoffsAutosave {
    isRowDirty: (tableRowId: number) => boolean;
    pendingPayoffDate: (tableRowId: number) => string | null | undefined;
    pendingPerDiem: (tableRowId: number) => number | null | undefined;
    setCellAnswerText: (
        tableRowId: number,
        column: VendorContractColumn,
        nextAnswerText: string
    ) => void;
    setPayoffDate: (tableRowId: number, nextValue: string | null) => void;
    setPerDiem: (tableRowId: number, nextValue: number | null) => void;
    flush: () => void;
}

interface PendingDetail<T> {
    touched: boolean;
    value: T;
}

const NOT_TOUCHED: PendingDetail<null> = { touched: false, value: null };

interface PendingCellEdit {
    column: VendorContractColumn;
    answerText: string;
}

interface DirtyRowState {
    cells: Map<string, PendingCellEdit>;
    payoffDate: PendingDetail<string | null>;
    perDiem: PendingDetail<number | null>;
}

export const resolveLatestThirdPartyPayoffCell = (
    row: ThirdPartyPayoff | undefined,
    freshCells: Map<string, VendorContractCell> | undefined,
    customColumnId: string
): VendorContractCell | undefined =>
    freshCells?.get(customColumnId) ??
    row?.cells.find((cell) => cell.customColumnId === customColumnId);

export const buildThirdPartyPayoffCellCommit = (
    column: VendorContractColumn,
    latestCell: VendorContractCell | undefined,
    answerText: string
): VendorContractCellCommit => ({
    customColumnId: column.customColumnId,
    cellId: latestCell?.cellId ?? null,
    version: latestCell?.version ?? null,
    answerText,
});

const MUTATION_TOAST_ID = 'third-party-payoffs-mutation';

export const thirdPartyPayoffsMutationToast = createFlowOfFundsMutationToast(
    THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID,
    MUTATION_TOAST_ID
);

export const useThirdPartyPayoffsAutosave = (
    rows: ThirdPartyPayoff[],
    saveRow: ThirdPartyPayoffRowSaveHandler
): ThirdPartyPayoffsAutosave => {
    const rowsRef = useRef(rows);
    rowsRef.current = rows;
    const saveRowRef = useRef(saveRow);
    saveRowRef.current = saveRow;

    const dirtyRowsRef = useRef(new Map<number, DirtyRowState>());
    const freshCellsRef = useRef(new Map<number, Map<string, VendorContractCell>>());
    const isSavingRef = useRef(false);
    const hasFollowUpFlushRef = useRef(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);
    const toastIdRef = useRef(
        `third-party-payoffs-autosave-${Math.random().toString(36).slice(2)}`
    );

    const [, setDirtyVersion] = useState(0);

    const bumpDirtyVersion = () => {
        if (isMountedRef.current) {
            setDirtyVersion((version) => version + 1);
        }
    };

    const clearDebounce = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    };

    const findRow = (tableRowId: number) =>
        rowsRef.current.find((row) => row.tableRowId === tableRowId);

    const latestCell = (tableRowId: number, customColumnId: string) =>
        resolveLatestThirdPartyPayoffCell(
            findRow(tableRowId),
            freshCellsRef.current.get(tableRowId),
            customColumnId
        );

    const getOrCreateDirtyRow = (tableRowId: number): DirtyRowState => {
        let dirty = dirtyRowsRef.current.get(tableRowId);
        if (!dirty) {
            dirty = {
                cells: new Map(),
                payoffDate: NOT_TOUCHED,
                perDiem: NOT_TOUCHED,
            };
            dirtyRowsRef.current.set(tableRowId, dirty);
        }
        return dirty;
    };

    const pruneCleanRow = (tableRowId: number, dirty: DirtyRowState) => {
        if (
            dirty.cells.size === 0 &&
            !dirty.payoffDate.touched &&
            !dirty.perDiem.touched
        ) {
            dirtyRowsRef.current.delete(tableRowId);
        }
    };

    const rememberFreshCells = (tableRowId: number, freshCells?: VendorContractCell[]) => {
        if (!freshCells) {
            return;
        }
        let overlay = freshCellsRef.current.get(tableRowId);
        if (!overlay) {
            overlay = new Map<string, VendorContractCell>();
            freshCellsRef.current.set(tableRowId, overlay);
        }
        freshCells.forEach((cell) => overlay!.set(cell.customColumnId, cell));
    };

    const clearSentEdits = (
        tableRowId: number,
        sentCells: Map<string, PendingCellEdit>,
        sentPayoffDate: PendingDetail<string | null>,
        sentPerDiem: PendingDetail<number | null>
    ) => {
        const dirty = dirtyRowsRef.current.get(tableRowId);
        if (!dirty) {
            return;
        }
        sentCells.forEach((sentEdit, customColumnId) => {
            const stillPending = dirty.cells.get(customColumnId);
            if (stillPending && stillPending.answerText === sentEdit.answerText) {
                dirty.cells.delete(customColumnId);
            }
        });
        if (dirty.payoffDate === sentPayoffDate) {
            dirty.payoffDate = NOT_TOUCHED;
        }
        if (dirty.perDiem === sentPerDiem) {
            dirty.perDiem = NOT_TOUCHED;
        }
        pruneCleanRow(tableRowId, dirty);
    };

    const saveDirtyRow = async (
        tableRowId: number
    ): Promise<ThirdPartyPayoffRowSaveOutcome | null> => {
        const dirty = dirtyRowsRef.current.get(tableRowId);
        if (!dirty) {
            return null;
        }
        const row = findRow(tableRowId);
        if (!row) {
            dirtyRowsRef.current.delete(tableRowId);
            freshCellsRef.current.delete(tableRowId);
            return null;
        }

        const sentCells = new Map(dirty.cells);
        const sentPayoffDate = dirty.payoffDate;
        const sentPerDiem = dirty.perDiem;

        const request: SaveThirdPartyPayoffRowRequest = {
            cells: Array.from(sentCells.values()).map((edit) =>
                buildThirdPartyPayoffCellCommit(
                    edit.column,
                    latestCell(tableRowId, edit.column.customColumnId),
                    edit.answerText
                )
            ),
            isPayoffDateProvided: sentPayoffDate.touched,
            payoffDate: sentPayoffDate.value,
            isPerDiemProvided: sentPerDiem.touched,
            perDiem: sentPerDiem.value,
        };

        const result = await saveRowRef.current(tableRowId, request);

        if (result.outcome === 'conflict') {
            dirtyRowsRef.current.delete(tableRowId);
            freshCellsRef.current.delete(tableRowId);
        } else if (result.outcome === 'ok') {
            rememberFreshCells(tableRowId, result.freshCells);
            clearSentEdits(tableRowId, sentCells, sentPayoffDate, sentPerDiem);
        }
        return result.outcome;
    };

    const flush = async () => {
        clearDebounce();
        if (isSavingRef.current) {
            hasFollowUpFlushRef.current = true;
            return;
        }
        if (dirtyRowsRef.current.size === 0) {
            return;
        }

        isSavingRef.current = true;
        if (isMountedRef.current) {
            showFlowOfFundsSavingToast(
                THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID,
                toastIdRef.current
            );
        }

        let hasError = false;
        let hasConflict = false;
        let savedRowCount = 0;
        const dirtyRowIds = Array.from(dirtyRowsRef.current.keys());
        for (const tableRowId of dirtyRowIds) {
            const outcome = await saveDirtyRow(tableRowId);
            if (outcome !== null) {
                savedRowCount += 1;
            }
            hasError = hasError || outcome === 'error';
            hasConflict = hasConflict || outcome === 'conflict';
        }

        isSavingRef.current = false;
        if (isMountedRef.current) {
            showFlowOfFundsCycleOutcomeToast(
                THIRD_PARTY_PAYOFFS_AUTOSAVE_TOAST_CONTAINER_ID,
                toastIdRef.current,
                hasError,
                hasConflict,
                savedRowCount
            );
        }
        bumpDirtyVersion();

        if (hasFollowUpFlushRef.current) {
            hasFollowUpFlushRef.current = false;
            flushRef.current();
        }
    };

    const flushRef = useRef(flush);
    flushRef.current = flush;

    useEffect(() => {
        isMountedRef.current = true;
        const toastId = toastIdRef.current;
        return () => {
            isMountedRef.current = false;
            clearTimeout(debounceTimerRef.current ?? undefined);
            flushRef.current();
            toast.dismiss(toastId);
        };
    }, []);

    const scheduleDebounce = () => {
        clearDebounce();
        debounceTimerRef.current = setTimeout(() => flushRef.current(), FLUSH_DEBOUNCE_MS);
    };

    const applyRowEdit = (
        tableRowId: number,
        assign: (dirty: DirtyRowState, row: ThirdPartyPayoff | undefined) => void
    ) => {
        const dirty = getOrCreateDirtyRow(tableRowId);
        assign(dirty, findRow(tableRowId));
        pruneCleanRow(tableRowId, dirty);
        bumpDirtyVersion();
        scheduleDebounce();
    };

    const setCellAnswerText = (
        tableRowId: number,
        column: VendorContractColumn,
        nextAnswerText: string
    ) =>
        applyRowEdit(tableRowId, (dirty) => {
            const serverValue =
                latestCell(tableRowId, column.customColumnId)?.answerText ?? '';
            if (nextAnswerText === serverValue) {
                dirty.cells.delete(column.customColumnId);
            } else {
                dirty.cells.set(column.customColumnId, { column, answerText: nextAnswerText });
            }
        });

    const setPayoffDate = (tableRowId: number, nextValue: string | null) =>
        applyRowEdit(tableRowId, (dirty, row) => {
            dirty.payoffDate =
                nextValue === (row?.payoffDate ?? null)
                    ? NOT_TOUCHED
                    : { touched: true, value: nextValue };
        });

    const setPerDiem = (tableRowId: number, nextValue: number | null) =>
        applyRowEdit(tableRowId, (dirty, row) => {
            dirty.perDiem =
                nextValue === (row?.perDiem ?? null)
                    ? NOT_TOUCHED
                    : { touched: true, value: nextValue };
        });

    return {
        isRowDirty: (tableRowId) => dirtyRowsRef.current.has(tableRowId),
        pendingPayoffDate: (tableRowId) => {
            const dirty = dirtyRowsRef.current.get(tableRowId);
            return dirty?.payoffDate.touched ? dirty.payoffDate.value : undefined;
        },
        pendingPerDiem: (tableRowId) => {
            const dirty = dirtyRowsRef.current.get(tableRowId);
            return dirty?.perDiem.touched ? dirty.perDiem.value : undefined;
        },
        setCellAnswerText,
        setPayoffDate,
        setPerDiem,
        flush: () => {
            flushRef.current();
        },
    };
};
